/**
 * =============================================================================
 * IDS CRM Web - Admin Settings Page
 * =============================================================================
 *
 * PURPOSE:
 * Admin settings page with tabs for Users, Currency Rates, and System Config.
 * Follows Apple-style design aesthetic.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { roleLabel, SPARE_PARTS_TAB_ROLES } from '../utils/permissions';
import EngineerDetail from './EngineerDetail';
import MenuSettings from './MenuSettings';
import AssetConfigSettings from './AssetConfigSettings';
import SupportConfigSettings from './SupportConfigSettings';
import EquipmentSettings from './EquipmentSettings';
import CheckFormSettings from './CheckFormSettings';
import InflowSettings from '../components/InflowSettings';
import M365Settings from '../components/M365Settings';
import NotificationSettings from './NotificationSettings';
import RateSettings from './RateSettings';
import GroupsSettings from './GroupsSettings';
import DocumentsSettings from './DocumentsSettings';
import SparePartsSettings from './SparePartsSettings';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import CountrySelect from '../components/CountrySelect';
import './AdminSettings.css';

// Primary-region options for team members. The team now spans the UK,
// mainland Europe and the States, so the list covers home regions plus
// continent-level definitions. Free-text column server-side — extending
// this list needs no migration.
const REGION_GROUPS = [
  ['UK & Ireland', [
    ['Scotland', 'Scotland'],
    ['North England', 'North England'],
    ['South England', 'South England'],
    ['Wales', 'Wales'],
    ['Northern Ireland', 'Northern Ireland'],
    ['Ireland', 'Ireland'],
    ['UK', 'UK (All)']
  ]],
  ['North America', [
    ['USA', 'USA'],
    ['Canada', 'Canada'],
    ['North America', 'North America (All)']
  ]],
  ['Worldwide', [
    ['Europe', 'Europe'],
    ['South America', 'South America'],
    ['Africa', 'Africa'],
    ['Asia', 'Asia'],
    ['Oceania', 'Oceania']
  ]]
];
const REGION_VALUES = REGION_GROUPS.flatMap(([, options]) => options.map(([value]) => value));

// Settings is open to EVERYONE since 2026-08-10 — which tabs render is the
// per-role question, answered here in one place. `roles: null` = every role
// (the personal Preferences group). The admin tabs keep their pre-existing
// visibility exactly: management sees all, team leaders the operational
// trio (Gareth 2026-08-03). The API gates every route independently — this
// registry is presentation, not the security boundary.
const MGMT = ['manager', 'admin'];
const MGMT_TL = ['manager', 'admin', 'team_leader'];
export const SETTINGS_TABS = [
  { key: 'users', label: 'Users', roles: MGMT },
  { key: 'currency', label: 'Currency Rates', roles: MGMT },
  { key: 'assets', label: 'Product Configuration', roles: MGMT },
  { key: 'equipment', label: 'Equipment', roles: MGMT_TL },
  { key: 'roles', label: 'Access Control', roles: MGMT },
  { key: 'groups', label: 'Groups', roles: MGMT },
  { key: 'support', label: 'Scheduled & Ticketed Support', roles: MGMT_TL },
  { key: 'check-forms', label: 'Check Forms', roles: MGMT_TL },
  { key: 'documents', label: 'Documents', roles: MGMT },
  { key: 'integrations', label: 'Integrations', roles: MGMT },
  { key: 'rates', label: 'Rates', roles: MGMT },
  // The spare parts list (spare parts, Push 2): operations coordinators,
  // managers and admins run it; team leaders see only the parts used that
  // need a price, which they price on the job or ticket page. Coordinators
  // land here, as it is their first tab.
  { key: 'spare-parts', label: 'Spare parts', roles: SPARE_PARTS_TAB_ROLES },
  // Personal tabs LAST so the first visible tab (the default landing) stays
  // Users for management and Equipment for team leaders, unchanged.
  // My Profile IS the profile page (EngineerDetail, self-scoped) — one
  // component, no duplicated forms (Gareth 2026-08-10).
  { key: 'profile', label: 'My Profile', roles: null },
  { key: 'menu', label: 'My Menu', roles: null },
  // Everyone chooses their own email and Teams notifications here; admins
  // also have the company settings and the Teams connection, managers and
  // admins the email log (Gareth 2026-10-14).
  { key: 'notifications', label: 'Notifications', roles: null }
];
export const settingsTabsForRole = (role) => SETTINGS_TABS.filter(tab => !tab.roles || tab.roles.includes(role));

function AdminSettings() {
  const { authFetch, user } = useAuth();
  const { toastElement, showToast } = useToast();
  const { confirmElement, requestConfirm } = useConfirm();
  const [searchParams] = useSearchParams();

  const visibleTabs = settingsTabsForRole(user?.role);
  const isManagementView = MGMT.includes(user?.role);
  // Deep links (?tab=menu from the mobile More sheet) land on the requested
  // tab only if this role can see it; anything else falls to the default.
  const requestedTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(
    visibleTabs.some(tab => tab.key === requestedTab) ? requestedTab : visibleTabs[0].key
  );
  // Clicking a ?tab= link while already ON /admin changes only the query
  // string — follow it (mount-time init alone would ignore it).
  useEffect(() => {
    if (requestedTab && settingsTabsForRole(user?.role).some(tab => tab.key === requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [requestedTab, user?.role]);
  const [users, setUsers] = useState([]);
  const [currencyRates, setCurrencyRates] = useState([]);
  const [roles, setRoles] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Microsoft SSO live? Then there are no passwords to set — accounts sign
  // in with their 365 identity and the server mints an unused random hash.
  const [ssoOn, setSsoOn] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await authFetch('/api/v1/auth/sso-config');
        const data = await response.json();
        if (!cancelled) setSsoOn(Boolean(data.enabled));
      } catch (err) { /* dormant/local — keep password fields */ }
    })();
    return () => { cancelled = true; };
  }, [authFetch]);

  // Users table sorting — client-side, the list is fully loaded. Same
  // header affordance as the Assets table (sortable-header / sort-indicator).
  const [userSort, setUserSort] = useState({ column: 'full_name', dir: 'asc' });

  const handleUserSort = (column) => {
    setUserSort(prev => prev.column === column
      ? { column, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { column, dir: 'asc' });
  };

  const userSortValue = useCallback((u, column) => {
    switch (column) {
      case 'role': return roleLabel(u.role);
      case 'is_active': return u.is_active ? 0 : 1;
      case 'current_status': return u.role === 'engineering' ? (u.current_status || 'off duty') : '';
      default: return u[column] || '';
    }
  }, []);

  const sortedUsers = useMemo(() => {
    const { column, dir } = userSort;
    const sign = dir === 'asc' ? 1 : -1;
    return [...users].sort((a, b) => {
      const av = userSortValue(a, column);
      const bv = userSortValue(b, column);
      const cmp = typeof av === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' });
      // Tie-break on name so equal groups (role, status) stay alphabetical
      return (cmp * sign) || String(a.full_name).localeCompare(String(b.full_name));
    });
  }, [users, userSort, userSortValue]);

  const UserSortHeader = ({ column, children }) => (
    <th onClick={() => handleUserSort(column)} className="sortable-header">
      {children}
      {userSort.column === column && (
        <span className="sort-indicator">{userSort.dir === 'asc' ? ' ▲' : ' ▼'}</span>
      )}
    </th>
  );

  // User form state
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    full_name: '',
    preferred_name: '',
    role: 'engineering',
    primary_region: 'Scotland',
    mobile: '',
    contract_type: 'full_time',
    home_postcode: '',
    home_lat: '',
    home_lng: '',
    is_active: true,
    is_approver: false,
    price_deviations_require_approval: false,
    approver_kind: 'none',
    approver_user_id: '',
    approver_group_id: '',
    can_view_own_pay: false,
    on_call_eligible: false
  });
  const [groupsList, setGroupsList] = useState([]);

  // Home-base coordinate lookup (Gareth 2026-08-02: the modal had lat/lng
  // boxes but no Find button - coordinates could only be typed by hand).
  const [homeGeocoding, setHomeGeocoding] = useState(false);
  const [homeGeocodeCountry, setHomeGeocodeCountry] = useState('GB');
  const [homeGeocodeMessage, setHomeGeocodeMessage] = useState({ type: '', text: '' });

  const findHomeCoordinates = async () => {
    setHomeGeocoding(true);
    setHomeGeocodeMessage({ type: '', text: '' });
    try {
      const response = await authFetch('/api/v1/geocode-address', {
        method: 'POST',
        body: JSON.stringify({
          postcode: userForm.home_postcode.trim(),
          country_code: homeGeocodeCountry.trim() || 'GB'
        })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setUserForm(prev => ({ ...prev, home_lat: String(data.latitude), home_lng: String(data.longitude) }));
        setHomeGeocodeMessage({ type: 'success', text: `Matched: ${data.displayName || 'postcode area'}. Save the user to store the coordinates.` });
      } else {
        setHomeGeocodeMessage({ type: 'error', text: data.error || data.message || 'No match found' });
      }
    } catch {
      setHomeGeocodeMessage({ type: 'error', text: 'Lookup failed - try again' });
    } finally {
      setHomeGeocoding(false);
    }
  };

  // Currency form state
  const [editingCurrency, setEditingCurrency] = useState(null);
  const [currencyRate, setCurrencyRate] = useState('');
  const [currencyForm, setCurrencyForm] = useState({
    currency_code: '',
    currency_name: '',
    rate_to_gbp: ''
  });

  const fetchData = useCallback(async () => {
    // Only management tabs need the admin datasets — nobody else's browser
    // should fire /admin/users and collect 403s just for opening Settings.
    if (!isManagementView) { setLoading(false); return; }
    try {
      setLoading(true);
      const [usersRes, ratesRes, rolesRes, groupsRes] = await Promise.all([
        authFetch('/api/v1/admin/users'),
        authFetch('/api/v1/admin/currency-rates'),
        authFetch('/api/v1/admin/roles'),
        authFetch('/api/v1/groups?purpose=operational')
      ]);

      const [usersData, ratesData, rolesData, groupsData] = await Promise.all([
        usersRes.json(),
        ratesRes.json(),
        rolesRes.json(),
        groupsRes.ok ? groupsRes.json() : { data: [] }
      ]);

      setUsers(usersData.data || []);
      setCurrencyRates(ratesData.data || []);
      setRoles(rolesData.data || {});
      setGroupsList(groupsData.data || []);
    } catch (err) {
      setError('Failed to load settings');
      console.error('Admin settings error:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch, isManagementView]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingUser
        ? `/api/v1/admin/users/${editingUser.user_id}`
        : '/api/v1/admin/users';
      const method = editingUser ? 'PUT' : 'POST';

      const body = { ...userForm };
      if (!body.password) delete body.password;
      // Map the approver-kind radio to the backend contract (one field or the other).
      if (body.approver_kind === 'group') {
        body.approver_group_id = body.approver_group_id || null;
        body.approver_user_id = undefined;
      } else if (body.approver_kind === 'user') {
        body.approver_user_id = body.approver_user_id || null;
        body.approver_group_id = undefined;
      } else {
        body.approver_user_id = null;
        body.approver_group_id = undefined;
      }
      delete body.approver_kind;
      // The own-pay grant is super-admin-only server-side; a manager's
      // edit must not carry the field at all or the whole save 403s.
      if (user?.role !== 'admin') delete body.can_view_own_pay;

      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message);
      }

      setShowUserModal(false);
      setEditingUser(null);
      resetUserForm();
      fetchData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleDeleteUser = (userId) => {
    requestConfirm({
      title: 'Deactivate user?',
      message: 'Are you sure you want to deactivate this user?',
      confirmLabel: 'Deactivate',
      danger: true,
      onConfirm: () => performDeleteUser(userId)
    });
  };

  const performDeleteUser = async (userId) => {
    try {
      const res = await authFetch(`/api/v1/admin/users/${userId}`, {
        method: 'DELETE'
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message);
      }

      fetchData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleEditUser = (u) => {
    setEditingUser(u);
    setUserForm({
      email: u.email,
      password: '',
      full_name: u.full_name,
      preferred_name: u.preferred_name || '',
      role: u.role,
      primary_region: u.primary_region || 'Scotland',
      mobile: u.mobile || '',
      contract_type: u.contract_type || 'full_time',
      home_postcode: u.home_postcode || '',
      home_lat: u.home_lat || '',
      home_lng: u.home_lng || '',
      is_active: u.is_active,
      is_approver: !!u.is_approver,
      price_deviations_require_approval: !!u.price_deviations_require_approval,
      approver_kind: u.approver_group_id ? 'group' : (u.approver_user_id ? 'user' : 'none'),
      approver_user_id: u.approver_user_id || '',
      approver_group_id: u.approver_group_id || '',
      can_view_own_pay: !!u.can_view_own_pay,
      on_call_eligible: !!u.on_call_eligible
    });
    setShowUserModal(true);
  };

  const resetUserForm = () => {
    setUserForm({
      email: '',
      password: '',
      full_name: '',
      preferred_name: '',
      role: 'engineering',
      primary_region: 'Scotland',
      mobile: '',
      contract_type: 'full_time',
      home_postcode: '',
      home_lat: '',
      home_lng: '',
      is_active: true,
      is_approver: false,
      price_deviations_require_approval: false,
      approver_kind: 'none',
      approver_user_id: '',
      approver_group_id: '',
      can_view_own_pay: false,
      on_call_eligible: false
    });
  };

  const handleCurrencyUpdate = async (code) => {
    try {
      const res = await authFetch(`/api/v1/admin/currency-rates/${code}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rate_to_gbp: parseFloat(currencyRate) })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message);
      }

      setEditingCurrency(null);
      setCurrencyRate('');
      fetchData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleCurrencyCreate = async (e) => {
    e.preventDefault();
    const code = currencyForm.currency_code.trim().toUpperCase();
    const name = currencyForm.currency_name.trim();
    const rate = parseFloat(currencyForm.rate_to_gbp);

    if (!/^[A-Z]{3}$/.test(code)) {
      showToast('Currency code must be a 3-letter ISO code, for example EUR or RON.', 'error');
      return;
    }

    if (!name || Number.isNaN(rate) || rate <= 0) {
      showToast('Currency name and a positive rate to GBP are required.', 'error');
      return;
    }

    try {
      const res = await authFetch('/api/v1/admin/currency-rates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currency_code: code,
          currency_name: name,
          rate_to_gbp: rate
        })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message);
      }

      setCurrencyForm({ currency_code: '', currency_name: '', rate_to_gbp: '' });
      fetchData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const getRoleChipClass = (role) => {
    switch (role) {
      case 'admin': return 'chip-red';
      case 'manager': return 'chip-purple';
      case 'financial': return 'chip-orange';
      case 'team_leader': return 'chip-teal';
      case 'operations_coordinator': return 'chip-yellow';
      case 'commercial': return 'chip-blue';
      case 'engineering': return 'chip-green';
      default: return 'chip-gray';
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  if (loading) {
    return <div className="admin-loading">Loading settings...</div>;
  }

  return (
    <div className="admin-page">
      {toastElement}
      {confirmElement}
      <div className="admin-header">
        <h1 className="admin-title">Settings</h1>
        <p className="admin-subtitle">
          {isManagementView || user?.role === 'team_leader'
            ? 'Manage users, currency rates, and system configuration'
            : (visibleTabs.length > 2 ? 'The spare parts list, your account and preferences' : 'Your account and preferences')}
        </p>
      </div>

      <div className="admin-tabs">
        {visibleTabs.map((tab, index) => (
          <React.Fragment key={tab.key}>
            {/* Visual seam between the admin group and the personal group */}
            {tab.roles === null && index > 0 && visibleTabs[index - 1].roles !== null && (
              <span className="tab-group-sep" aria-hidden="true" />
            )}
            <button
              className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          </React.Fragment>
        ))}
      </div>

      {error && <div className="admin-error">{error}</div>}

      {activeTab === 'profile' && <EngineerDetail userId={user.userId} embedded />}

      {activeTab === 'menu' && <MenuSettings />}

      {activeTab === 'assets' && <AssetConfigSettings />}

      {activeTab === 'integrations' && (
        <>
          <InflowSettings />
          <M365Settings />
        </>
      )}

      {activeTab === 'notifications' && <NotificationSettings />}

      {activeTab === 'rates' && <RateSettings />}

      {activeTab === 'spare-parts' && <SparePartsSettings />}

      {activeTab === 'groups' && <GroupsSettings />}

      {activeTab === 'documents' && <DocumentsSettings />}

      {activeTab === 'equipment' && <EquipmentSettings />}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="admin-section">
          <div className="section-header">
            <h2>Team Members</h2>
            <div className="section-actions">
              <p className="section-hint">Users with Engineer role appear on the Map and can be assigned tickets/tasks</p>
              {user?.role === 'admin' && (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => {
                    resetUserForm();
                    setEditingUser(null);
                    setShowUserModal(true);
                  }}
                >
                  + Add User
                </button>
              )}
            </div>
          </div>

          <div className="users-table-wrap">
            <table className="users-table">
              <thead>
                <tr>
                  <UserSortHeader column="full_name">Member</UserSortHeader>
                  <UserSortHeader column="role">Role</UserSortHeader>
                  <UserSortHeader column="primary_region">Region</UserSortHeader>
                  <th>Mobile</th>
                  <UserSortHeader column="is_active">Status</UserSortHeader>
                  <UserSortHeader column="current_status">Current</UserSortHeader>
                  {user?.role === 'admin' && <th></th>}
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map(u => (
                  <tr key={u.user_id} className={!u.is_active ? 'inactive' : ''}>
                    <td>
                      <div className="user-cell">
                        <div className={`user-avatar role-${u.role}`}>
                          {getInitials(u.full_name)}
                        </div>
                        <div className="user-info">
                          <div className="user-name">{u.full_name}</div>
                          <div className="user-email">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`chip ${getRoleChipClass(u.role)}`}>
                        {roleLabel(u.role)}
                      </span>
                    </td>
                    <td>{u.primary_region || '-'}</td>
                    <td>{u.mobile || '-'}</td>
                    <td>
                      <span className={`status-dot ${u.is_active ? 'active' : 'inactive'}`}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="user-current">
                      {u.role === 'engineering' ? (u.current_status?.replace(/_/g, ' ') || 'off duty') : '-'}
                    </td>
                    {user?.role === 'admin' && (
                      <td className="user-row-actions">
                        <button className="btn-secondary" onClick={() => handleEditUser(u)}>
                          Edit
                        </button>
                        {u.user_id !== user.userId && (
                          <button className="btn-danger" onClick={() => handleDeleteUser(u.user_id)}>
                            Remove
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Currency Rates Tab */}
      {activeTab === 'currency' && (
        <div className="admin-section">
          <div className="section-header">
            <h2>Currency Exchange Rates</h2>
            <p className="section-desc">Rates to convert foreign currencies to GBP for reporting</p>
          </div>

          {user?.role === 'admin' && (
            <form className="currency-add-form" onSubmit={handleCurrencyCreate}>
              <div className="form-group">
                <label htmlFor="currency_code">Code</label>
                <input
                  id="currency_code"
                  value={currencyForm.currency_code}
                  onChange={(e) => setCurrencyForm({
                    ...currencyForm,
                    currency_code: e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3)
                  })}
                  placeholder="RON"
                  maxLength={3}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="currency_name">Currency Name</label>
                <input
                  id="currency_name"
                  value={currencyForm.currency_name}
                  onChange={(e) => setCurrencyForm({ ...currencyForm, currency_name: e.target.value })}
                  placeholder="Romanian Leu"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="rate_to_gbp">Rate to GBP</label>
                <input
                  id="rate_to_gbp"
                  type="number"
                  step="0.000001"
                  min="0.000001"
                  value={currencyForm.rate_to_gbp}
                  onChange={(e) => setCurrencyForm({ ...currencyForm, rate_to_gbp: e.target.value })}
                  placeholder="0.170000"
                  required
                />
              </div>
              <button type="submit" className="btn-primary">Add Currency</button>
            </form>
          )}

          <div className="currency-table">
            <div className="currency-row header">
              <span>Currency</span>
              <span>Rate to GBP</span>
              <span>Example</span>
              <span>Last Updated</span>
              <span></span>
            </div>
            {currencyRates.map(cr => (
              <div key={cr.currency_code} className="currency-row">
                <span className="currency-code">
                  <strong>{cr.currency_code}</strong>
                  <span className="currency-name">{cr.currency_name}</span>
                </span>
                <span className="currency-rate">
                  {editingCurrency === cr.currency_code ? (
                    <input
                      type="number"
                      step="0.0001"
                      value={currencyRate}
                      onChange={(e) => setCurrencyRate(e.target.value)}
                      className="rate-input"
                      autoFocus
                    />
                  ) : (
                    cr.rate_to_gbp
                  )}
                </span>
                <span className="currency-example">
                  {cr.currency_code === 'GBP'
                    ? '1.00 = £1.00'
                    : `1.00 = £${parseFloat(cr.rate_to_gbp).toFixed(2)}`
                  }
                </span>
                <span className="currency-updated">
                  {cr.updated_at
                    ? new Date(cr.updated_at).toLocaleDateString()
                    : '-'}
                </span>
                <span className="currency-actions">
                  {cr.currency_code !== 'GBP' && user?.role === 'admin' && (
                    editingCurrency === cr.currency_code ? (
                      <>
                        <button
                          className="btn-small btn-primary"
                          onClick={() => handleCurrencyUpdate(cr.currency_code)}
                        >
                          Save
                        </button>
                        <button
                          className="btn-small btn-secondary"
                          onClick={() => {
                            setEditingCurrency(null);
                            setCurrencyRate('');
                          }}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn-small btn-secondary"
                        onClick={() => {
                          setEditingCurrency(cr.currency_code);
                          setCurrencyRate(cr.rate_to_gbp.toString());
                        }}
                      >
                        Edit
                      </button>
                    )
                  )}
                </span>
              </div>
            ))}
          </div>

          <div className="currency-info">
            <p>
              <strong>Note:</strong> Pipeline values are automatically converted to GBP using these rates.
              Update rates regularly to maintain accurate reporting.
            </p>
          </div>
        </div>
      )}

      {/* Access Control Tab */}
      {activeTab === 'roles' && (
        <div className="admin-section">
          <div className="section-header">
            <h2>Role Permissions</h2>
            <p className="section-desc">Access rights for each user role</p>
          </div>

          <div className="roles-grid">
            {Object.entries(roles).map(([key, role]) => (
              <div key={key} className="role-card">
                <div className="role-header">
                  <span className={`chip ${getRoleChipClass(key)}`}>{roleLabel(key)}</span>
                  <h3>{role.name}</h3>
                </div>
                <p className="role-desc">{role.description}</p>

                <div className="permissions-section">
                  <h4>Can Access</h4>
                  <div className="permissions-list">
                    {role.permissions.map(p => (
                      <span key={p} className="permission-chip allowed">
                        {p.replace(/_/g, ' ').replace(':', ' ')}
                      </span>
                    ))}
                  </div>
                </div>

                {role.restricted && (
                  <div className="permissions-section">
                    <h4>Restricted</h4>
                    <div className="permissions-list">
                      {role.restricted.map(p => (
                        <span key={p} className="permission-chip restricted">
                          {p.replace(/_/g, ' ').replace(':', ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'support' && <SupportConfigSettings />}

      {activeTab === 'check-forms' && <CheckFormSettings />}

      {/* User Modal */}
      {showUserModal && (
        <div className="modal-backdrop" onClick={() => setShowUserModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingUser ? 'Edit User' : 'Add New User'}</h2>
              <button type="button" className="modal-close" onClick={() => setShowUserModal(false)}>
                &times;
              </button>
            </div>

            <form onSubmit={handleUserSubmit} className="modal-body" autoComplete="off">
              <div className="form-row">
                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    value={userForm.full_name}
                    onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })}
                    autoComplete="off"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Preferred Name</label>
                  <input
                    type="text"
                    value={userForm.preferred_name}
                    onChange={(e) => setUserForm({ ...userForm, preferred_name: e.target.value })}
                    autoComplete="off"
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    autoComplete="new-email"
                    required
                  />
                  {ssoOn && (
                    <small className="field-hint">
                      Must exactly match their Microsoft 365 email — sign-in links the two.
                    </small>
                  )}
                </div>
                {!ssoOn && (
                  <div className="form-group">
                    <label>{editingUser ? 'New Password' : 'Password *'}</label>
                    <input
                      type="password"
                      value={userForm.password}
                      onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                      autoComplete="new-password"
                      required={!editingUser}
                      placeholder={editingUser ? 'Leave blank to keep current' : ''}
                    />
                  </div>
                )}
              </div>
              {ssoOn && !editingUser && (
                <p className="sso-user-note">
                  No password needed — they sign in with their Microsoft account
                  once this user is created.
                </p>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Role *</label>
                  <select
                    value={userForm.role}
                    onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                  >
                    <option value="engineering">Engineering</option>
                    <option value="team_leader">Team Leader</option>
                    <option value="operations_coordinator">Operations Coordinator</option>
                    <option value="commercial">Commercial</option>
                    <option value="financial">Financial</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Super Admin</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Primary Region</label>
                  <select
                    value={userForm.primary_region}
                    onChange={(e) => setUserForm({ ...userForm, primary_region: e.target.value })}
                  >
                    {/* Off-list value (e.g. free-typed on the engineer profile):
                        keep it selectable so editing doesn't silently rewrite it */}
                    {userForm.primary_region && !REGION_VALUES.includes(userForm.primary_region) && (
                      <option value={userForm.primary_region}>{userForm.primary_region}</option>
                    )}
                    {REGION_GROUPS.map(([groupLabel, options]) => (
                      <optgroup key={groupLabel} label={groupLabel}>
                        {options.map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Mobile</label>
                  <input
                    type="tel"
                    value={userForm.mobile}
                    onChange={(e) => setUserForm({ ...userForm, mobile: e.target.value })}
                    placeholder="+44 7xxx xxxxxx"
                  />
                </div>
                <div className="form-group">
                  <label>Contract Type</label>
                  <select
                    value={userForm.contract_type}
                    onChange={(e) => setUserForm({ ...userForm, contract_type: e.target.value })}
                  >
                    <option value="full_time">Full Time</option>
                    <option value="part_time">Part Time</option>
                    <option value="contractor">Contractor</option>
                  </select>
                </div>
              </div>

              {userForm.role === 'engineering' && (
                <>
                  <div className="form-section-label">Location (for Map)</div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Home Postcode</label>
                      <input
                        type="text"
                        value={userForm.home_postcode}
                        onChange={(e) => setUserForm({ ...userForm, home_postcode: e.target.value })}
                        placeholder="FK9 4NF"
                      />
                    </div>
                    <div className="form-group">
                      <label>Latitude</label>
                      <input
                        type="number"
                        step="0.0001"
                        value={userForm.home_lat}
                        onChange={(e) => setUserForm({ ...userForm, home_lat: e.target.value })}
                        placeholder="56.1496"
                      />
                    </div>
                    <div className="form-group">
                      <label>Longitude</label>
                      <input
                        type="number"
                        step="0.0001"
                        value={userForm.home_lng}
                        onChange={(e) => setUserForm({ ...userForm, home_lng: e.target.value })}
                        placeholder="-3.9218"
                      />
                    </div>
                  </div>
                  <div className="form-row home-geocode-row">
                    <div className="form-group">
                      <label>Country (for lookup)</label>
                      <CountrySelect
                        value={homeGeocodeCountry}
                        onChange={(e) => setHomeGeocodeCountry(e.target.value)}
                      />
                    </div>
                    <div className="form-group home-geocode-action">
                      <button
                        type="button"
                        className="btn-secondary"
                        disabled={homeGeocoding || !userForm.home_postcode.trim()}
                        onClick={findHomeCoordinates}
                      >
                        {homeGeocoding ? 'Looking up...' : 'Find coordinates'}
                      </button>
                    </div>
                  </div>
                  {homeGeocodeMessage.text && (
                    <div className={`home-geocode-message ${homeGeocodeMessage.type}`}>
                      {homeGeocodeMessage.text}
                    </div>
                  )}
                </>
              )}

              {editingUser && (
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={userForm.is_active}
                      onChange={(e) => setUserForm({ ...userForm, is_active: e.target.checked })}
                    />
                    User is active
                  </label>
                </div>
              )}

              {user?.role === 'admin' && (
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={userForm.can_view_own_pay}
                      onChange={(e) => setUserForm({ ...userForm, can_view_own_pay: e.target.checked })}
                    />
                    Can view own pay report (their on-call standby, call-out and overtime figures — nobody else's)
                  </label>
                </div>
              )}

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={userForm.on_call_eligible}
                    onChange={(e) => setUserForm({ ...userForm, on_call_eligible: e.target.checked })}
                  />
                  Include on the on-call rota (any role — appears in the rota dropdown and can hold cover)
                </label>
              </div>

              <div className="form-group approver-settings">
                <label className="section-label">Pricing approval</label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={userForm.is_approver}
                    onChange={(e) => setUserForm({ ...userForm, is_approver: e.target.checked })}
                  />
                  Can approve price deviations (is an approver)
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={userForm.price_deviations_require_approval}
                    onChange={(e) => setUserForm({ ...userForm, price_deviations_require_approval: e.target.checked })}
                  />
                  This user's price deviations require approval
                </label>
                {userForm.price_deviations_require_approval && (
                  <div className="approver-route">
                    <label>Approved by</label>
                    <select
                      value={userForm.approver_kind}
                      onChange={(e) => setUserForm({ ...userForm, approver_kind: e.target.value })}
                    >
                      <option value="none">— select —</option>
                      <option value="user">A specific user</option>
                      <option value="group">A group</option>
                    </select>
                    {userForm.approver_kind === 'user' && (
                      <select
                        value={userForm.approver_user_id}
                        onChange={(e) => setUserForm({ ...userForm, approver_user_id: e.target.value })}
                      >
                        <option value="">— choose approver —</option>
                        {users.filter(u => u.is_approver && u.user_id !== editingUser?.user_id).map(u => (
                          <option key={u.user_id} value={u.user_id}>{u.full_name}</option>
                        ))}
                      </select>
                    )}
                    {userForm.approver_kind === 'group' && (
                      <select
                        value={userForm.approver_group_id}
                        onChange={(e) => setUserForm({ ...userForm, approver_group_id: e.target.value })}
                      >
                        <option value="">— choose group —</option>
                        {groupsList.map(g => (
                          <option key={g.group_id} value={g.group_id}>{g.name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowUserModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingUser ? 'Save Changes' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminSettings;
